import { memo, useCallback, useMemo } from "react";
import {
  TConfigItemType,
  TVerificationAppFieldConfigItem,
} from "../../../app/models";
import { TDocument, TDocumentData } from "../../models";
import { saveDocumentDataForDocument } from "../../network";
import {
  FILES_NAMESPACE,
  TFileIntentMap,
  buildInitialFileIntents,
  processFileIntents,
} from "../../utils/fileIntents";
import { cleanupConfigFieldValues } from "../../utils/cleanupConfigFieldValues";
import { getEditablePrimaryDocumentData } from "../../utils/pendingDocument";
import { BinaryContent } from "../BinaryContent";
import { DocumentImgCard } from "../DocumentImgCard";
import { AccordionShell } from "./AccordionShell";

function splitData(data: TDocumentData[]) {
  const binary: TDocumentData[] = [];
  const file: TDocumentData[] = [];
  for (const item of data) {
    if (item.type === "binary") binary.push(item);
    else if (item.type === "file") file.push(item);
  }
  return { binary, file };
}

export type DefaultDocumentRendererProps = {
  document: TDocument;
  requestId: string;
  title: string;
  configFields?: TVerificationAppFieldConfigItem[];
  companyRole: string;
  canEdit: boolean;
  comments: Record<string, string>;
  addComment: (userDocumentId: string, comment: string) => void;
  onRefreshDocuments?: () => void | Promise<void>;
  defaultExpanded?: boolean;
};

export const DefaultDocumentRenderer = memo(
  (props: DefaultDocumentRendererProps) => {
    const {
      document,
      requestId,
      title,
      configFields,
      companyRole,
      canEdit,
      comments,
      addComment,
      onRefreshDocuments,
      defaultExpanded,
    } = props;
    const { binary, file } = useMemo(
      () => splitData(document.data),
      [document.data],
    );

    const loadedMainEntry = useMemo(
      () => binary.find((d) => d.dataId === document.document.documentType),
      [binary, document.document.documentType],
    );
    const hasConfiguredPayloadFields = !!configFields?.some(
      (field) => field.type !== TConfigItemType.FILE_PICKER,
    );
    const mainEntry = useMemo(
      () =>
        loadedMainEntry ??
        (canEdit && hasConfiguredPayloadFields
          ? getEditablePrimaryDocumentData(document)
          : undefined),
      [canEdit, document, hasConfiguredPayloadFields, loadedMainEntry],
    );
    const renderedConfigFields = useMemo(() => {
      if (!configFields || configFields.length === 0) {
        return undefined;
      }
      if (mainEntry) {
        return configFields;
      }
      return configFields.filter(
        (field) => field.type === TConfigItemType.FILE_PICKER,
      );
    }, [configFields, mainEntry]);
    const useConfigFiles = !!(
      renderedConfigFields && renderedConfigFields.length > 0
    );

    const hasFilePickerFields = !!renderedConfigFields?.some(
      (field) => field.type === TConfigItemType.FILE_PICKER,
    );
    const canEditMainPayload = canEdit && !!mainEntry;
    const canEditFiles = canEdit && hasFilePickerFields;
    const canEditAccordion = canEditMainPayload || canEditFiles;

    const initialValues = useMemo(() => {
      const main = (mainEntry?.data as Record<string, unknown> | undefined) ?? {};
      if (!hasFilePickerFields) {
        return main;
      }
      return {
        ...main,
        [FILES_NAMESPACE]: buildInitialFileIntents(file),
      };
    }, [mainEntry?.data, hasFilePickerFields, file]);

    const mainDataId = mainEntry?.dataId;
    const onSavePayload = useCallback(
      async (values: Record<string, unknown>) => {
        const { [FILES_NAMESPACE]: files, ...mainValues } = values as Record<
          string,
          unknown
        > & { [FILES_NAMESPACE]?: TFileIntentMap };

        let effectiveDocument = document.document;
        if (canEditMainPayload && mainDataId) {
          effectiveDocument = await saveDocumentDataForDocument(
            document.document,
            requestId,
            mainDataId,
            cleanupConfigFieldValues(mainValues, renderedConfigFields)
          );
        }
        if (canEditFiles) {
          await processFileIntents(effectiveDocument.documentId, files);
        }
        return effectiveDocument.documentId;
      },
      [
        canEditMainPayload,
        canEditFiles,
        document.document,
        mainDataId,
        renderedConfigFields,
        requestId,
      ],
    );

    return (
      <AccordionShell
        title={title}
        document={document}
        companyRole={companyRole}
        comments={comments}
        addComment={addComment}
        initialValues={initialValues}
        canEditPayload={canEditAccordion}
        requiresPayloadSave={mainEntry?.id === ""}
        onSavePayload={canEditAccordion ? onSavePayload : undefined}
        onSaveSuccess={onRefreshDocuments}
        defaultExpanded={defaultExpanded}
      >
        {(mainEntry || useConfigFiles) && (
          <BinaryContent
            allDisabled={!canEditAccordion}
            data={mainEntry}
            configFields={renderedConfigFields}
            fileItems={useConfigFiles ? file : undefined}
            requestId={useConfigFiles ? requestId : undefined}
            documentInfo={useConfigFiles ? document.document : undefined}
          />
        )}
        {!useConfigFiles && file.length > 0 && (
          <DocumentImgCard
            fileItems={file}
            requestId={requestId}
            document={document.document}
          />
        )}
      </AccordionShell>
    );
  },
);
